import { useEffect, useState } from "react";
import { fetchHipoPool } from "../lib/api";
import EmployeeDetailModal from "./EmployeeDetailModal";
import { Loader2, Star, Building2, ArrowUpRight, Award } from "lucide-react";

export default function HiPoPoolPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailEmpId, setDetailEmpId] = useState(null);

  useEffect(() => {
    setLoading(true);
    fetchHipoPool()
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="card-surface p-8 flex items-center justify-center gap-3 text-slate-500">
        <Loader2 className="w-4 h-4 animate-spin" /> HiPo havuzu yükleniyor...
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="space-y-5" data-testid="hipo-pool-panel">
      {/* KPI row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card-surface p-5 border-l-4 border-indigo-500">
          <div className="label-eyebrow flex items-center gap-2">
            <Star className="w-3.5 h-3.5 text-indigo-600" /> HiPo Havuzu
          </div>
          <div className="text-3xl font-bold text-indigo-600 mt-1">{data.total}</div>
          <div className="text-xs text-slate-500 mt-1">Toplam yüksek potansiyelli çalışan</div>
        </div>
        <div className="card-surface p-5 border-l-4 border-emerald-500">
          <div className="label-eyebrow flex items-center gap-2">
            <Award className="w-3.5 h-3.5 text-emerald-600" /> Gelişim Programında
          </div>
          <div className="text-3xl font-bold text-emerald-600 mt-1">{data.in_development_program}</div>
          <div className="text-xs text-slate-500 mt-1">Özel programa dahil edilmiş çalışan</div>
        </div>
        <div className="card-surface p-5 border-l-4 border-sky-500">
          <div className="label-eyebrow flex items-center gap-2">
            <Building2 className="w-3.5 h-3.5 text-sky-600" /> Kapsanan Departman
          </div>
          <div className="text-3xl font-bold text-sky-600 mt-1">{data.by_department?.length || 0}</div>
          <div className="text-xs text-slate-500 mt-1">HiPo temsili olan birim sayısı</div>
        </div>
      </div>

      {/* Departmental distribution */}
      {data.by_department?.length > 0 && (
        <div className="card-surface p-5">
          <div className="label-eyebrow mb-3">Departmanlara Göre Dağılım</div>
          <div className="flex flex-wrap gap-2">
            {data.by_department.map((d) => (
              <div key={d.department} className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-50 border border-slate-100">
                <span className="text-sm text-slate-700">{d.department}</span>
                <span className="text-xs font-semibold text-indigo-600">{d.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* HiPo list */}
      <div className="card-surface p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="label-eyebrow flex items-center gap-2">
            <Star className="w-3.5 h-3.5 text-indigo-600" /> Yüksek Potansiyelli Çalışanlar
          </div>
          <div className="text-xs text-slate-500">{data.pool?.length || 0} kişi</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {(data.pool || []).map((p) => (
            <button
              type="button"
              key={p.id}
              onClick={() => setDetailEmpId(p.id)}
              className="flex items-start gap-3 p-3 rounded-lg bg-white border border-slate-100 hover:border-indigo-200 hover:shadow-sm transition-all text-left"
              data-testid={`hipo-employee-${p.id}`}
            >
              <img src={p.avatar} alt={p.name} className="w-11 h-11 rounded-full object-cover" onError={(e) => (e.currentTarget.style.visibility = "hidden")} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <div className="text-sm font-semibold text-slate-800 truncate">{p.name}</div>
                  {p.in_development_program && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 font-medium">Programda</span>
                  )}
                </div>
                <div className="text-xs text-slate-500 truncate">{p.title} · {p.department}</div>
                <div className="flex items-center gap-3 mt-1.5 text-[11px] text-slate-500">
                  <span>Perf: <b className="text-slate-700">{p.performance_rating?.toFixed(1) || "–"}</b></span>
                  <span>Bağlılık: <b className="text-slate-700">{p.engagement_score || "–"}</b></span>
                  <span>Kıdem: <b className="text-slate-700">{p.tenure_years} yıl</b></span>
                </div>
                {p.next_target && (
                  <div className="mt-2 flex items-center gap-1.5 text-[11px] text-indigo-600">
                    <ArrowUpRight className="w-3 h-3" />
                    <span>Hedef: <b>{p.next_target.title}</b> ({p.next_target.band})</span>
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>
      </div>
      {/* Detail modal */}
      <EmployeeDetailModal
        employeeId={detailEmpId}
        open={!!detailEmpId}
        onOpenChange={(v) => !v && setDetailEmpId(null)}
      />
    </div>
  );
}
